// AppContext.js
import React, { createContext, useState, useMemo } from 'react';
import Cookies from 'js-cookie';

export const AppContext = createContext();

/**
 * Decodes JWT token from cookie and returns user info
 * @returns {Object} User info or null if no valid token
 */
const getUserFromToken = () => {
  try {
    const token = Cookies.get('userToken');
    if (!token) return null;

    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decoded = JSON.parse(atob(paddedPayload));

    return {
      userId: decoded.sub ? parseInt(decoded.sub) : null,
      email: decoded.email || null,
      name: decoded.name || null,
      role: decoded.role || 'Doctor',
      orgId: decoded.orgId ? parseInt(decoded.orgId) : null,
    };
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

export const AppProvider = ({ children }) => {
  const [patientData, setPatientData] = useState({});
  const [diagnosis, setDiagnosis] = useState('');
  const [currentUser, setCurrentUser] = useState(getUserFromToken());
  const [analysisResults, setAnalysisResults] = useState(null);
  const [currentCase, setCurrentCase] = useState(null);
  const [currentPatient, setCurrentPatient] = useState(null);

  // Memoize user utilities to prevent unnecessary recalculations
  const userUtils = useMemo(
    () => ({
      getUserId: () => currentUser?.userId,
      getEmail: () => currentUser?.email,
      getName: () => currentUser?.name,
      getRole: () => currentUser?.role,
      getOrgId: () => currentUser?.orgId,
      isAuthenticated: () => !!currentUser?.userId,
      isAdmin: () => currentUser?.role === 'Admin',
      updateUser: (newUser) => setCurrentUser(newUser),
    }),
    [currentUser]
  );

  const value = {
    // Patient data (from form)
    patientData,
    setPatientData,

    // Diagnosis results
    diagnosis,
    setDiagnosis,

    // Analysis results
    analysisResults,
    setAnalysisResults,

    // Context state
    currentCase,
    setCurrentCase,
    currentPatient,
    setCurrentPatient,

    // Current authenticated user
    currentUser,
    setCurrentUser,

    // User utilities
    ...userUtils,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
